import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardBody, CardFooter, Dialog, Typography } from "@material-tailwind/react";
import Label from "components/Label/Label";
import { AnimatePresence, motion } from "framer-motion";
import { useAppDispatch } from "hooks/hooks";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { addFeature } from "redux/features/Admin/PlanFeature/planFeatureSlice";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import Textarea from "shared/Textarea/Textarea";
import { z } from "zod";
import { EASING } from "../../utils/easingUtils";

// Zod schema for plan data
const planSchema = z.object({
  description: z.string().min(1, "Le nom est requis"),
});

type PlanSchemaType = z.infer<typeof planSchema>;

// Animation variants
const dialogVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: EASING.easeOut } },
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.2, ease: EASING.easeIn } },
};

const fieldVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: EASING.easeOut } },
};

interface EntityDialogFormProps {
  open: boolean;
  handleClose?: () => void;
  onSubmitSuccess?: () => void;
  isLoading?: boolean;
  addfn?: typeof addFeature;
  addfnTitle?: string;
}

export const PlanFeatureDialogForm: React.FC<EntityDialogFormProps> = React.memo(
  ({
    addfn,
    open,
    addfnTitle = "Ajouter un forfait",
    onSubmitSuccess,
    handleClose = () => {},
    isLoading = false,
  }) => {
    const {
      register,
      handleSubmit,
      formState: { errors },
      reset,
    } = useForm<PlanSchemaType>({
      resolver: zodResolver(planSchema),
      defaultValues: {
        description: "",
      },
    });
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);

    // Reset form when dialog opens/closes
    useEffect(() => {
      if (open) {
        reset({
          description: "",
        });
      }
    }, [open, reset]);

    const onSubmit = async (data: PlanSchemaType) => {
      try {
        setLoading(true);

        if (addfn) {
          await dispatch(addfn(data)).unwrap();
          //toast.success("Plan créé avec succès");
        }

        onSubmitSuccess?.();
        handleClose();
        reset();
      } catch (error: any) {
        toast.error(error.message || "Une erreur est survenue");
      } finally {
        setLoading(false);
      }
    };

    return (
      <AnimatePresence>
        {open && (
          <Dialog
            size="sm"
            open={open}
            handler={handleClose}
            className="w-full max-w-md mx-auto transition-all duration-300 bg-white shadow-2xl rounded-2xl dark:bg-gray-900"
            aria-labelledby="dialog-title"
          >
            <motion.div variants={dialogVariants} initial="hidden" animate="visible" exit="exit">
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <Card className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl">
                  <CardBody className="flex flex-col gap-6 p-6 max-h-[50vh] overflow-y-auto bg-white dark:bg-gray-900 rounded-t-2xl">
                    <Typography
                      id="dialog-title"
                      variant="h5"
                      className="p-4 font-bold tracking-tight text-center text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl animate-fade-in"
                    >
                      {addfnTitle}
                    </Typography>
                    {/* Name */}
                    <motion.div variants={fieldVariants} initial="hidden" animate="visible">
                      <Label className="font-semibold text-gray-700 dark:text-gray-200">Description :</Label>
                      <div className="relative mt-2">
                        <Textarea
                          //size="lg"
                          //type="text"
                          placeholder="Ex. Visibilité plateforme"
                          {...register("description")}
                          aria-invalid={!!errors.description}
                          aria-describedby="description-error"
                          className="text-gray-900 transition-shadow duration-200 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 dark:text-white hover:shadow-md focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                        />
                      </div>
                      <AnimatePresence>
                        {errors.description && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                          >
                            <Typography
                              id="description-error"
                              variant="small"
                              color="red"
                              className="mt-1 text-xs text-red-500 dark:text-red-400"
                            >
                              {errors.description.message}
                            </Typography>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  </CardBody>
                  <CardFooter className="flex justify-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-b-2xl">
                    <ButtonSecondary
                      type="button"
                      onClick={handleClose}
                      className="px-6 py-2 text-gray-900 transition-colors duration-200 bg-gray-200 rounded-full dark:bg-gray-700 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600"
                      disabled={loading || isLoading}
                    >
                      Annuler
                    </ButtonSecondary>
                    <ButtonSecondary
                      type="submit"
                      className="px-6 py-2 text-white transition-all duration-300 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-600 dark:to-indigo-700 hover:from-blue-600 hover:to-indigo-700 dark:hover:from-blue-700 dark:hover:to-indigo-800 hover:shadow-lg"
                      disabled={loading || isLoading}
                      loading={loading || isLoading}
                    >
                      Confirmer
                    </ButtonSecondary>
                  </CardFooter>
                </Card>
              </form>
            </motion.div>
          </Dialog>
        )}
      </AnimatePresence>
    );
  }
);
